import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logoD.png";
import { FaBars, FaChevronDown } from "react-icons/fa";
import { IoCloseCircleOutline, IoCallOutline } from "react-icons/io5";
import { LuDownload } from "react-icons/lu";
import { MdLocalPhone } from "react-icons/md";
import Popup from "./Popup";

const navItems = [
  ["Home", "home"],
  ["About", "about"],
  ["Ongoing Projects", "ongoingProjects"],
  ["Completed Projects", "completedProjects"],
  // ["Upcoming Projects", "upcomingProjects"],
  ["Layout", "plots"],
  ["Contact", "contact"],
];

const Navbar = () => {
  const [isProjectsHovered, setIsProjectsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const isClickScrollingRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (!isHomePage) return; // Skip scroll spy on non-home pages

    const handleScrollSpy = () => {
      // Do not run scroll spy calculations while smooth-scrolling from a click
      if (isClickScrollingRef.current) return;

      // 1. Check if the user is at the bottom of the page
      const isAtBottom =
        window.innerHeight + window.pageYOffset >=
        document.documentElement.scrollHeight - 60;

      if (isAtBottom) {
        setActiveSection("contact");
        return;
      }

      // 2. Regular offset-based detection
      const scrollPosition = window.pageYOffset + 150; // offset buffer

      for (let i = navItems.length - 1; i >= 0; i--) {
        const [, id] = navItems[i];
        const section = document.getElementById(id);
        if (section) {
          if (scrollPosition >= section.offsetTop) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScrollSpy, { passive: true });
    return () => window.removeEventListener("scroll", handleScrollSpy);
  }, [isHomePage]);

  // Helper: scroll to a section on the home page
  const scrollToSection = (targetId) => {
    const target = document.getElementById(targetId);
    if (target) {
      const y = target.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleScroll = (e, targetId) => {
    e.preventDefault();
    setActiveSection(targetId);
    if (isOpen) setIsOpen(false);
    setIsProjectsHovered(false);

    // If already on home page — just smooth scroll
    if (isHomePage) {
      isClickScrollingRef.current = true;
      setTimeout(() => {
        isClickScrollingRef.current = false;
      }, 800);

      scrollToSection(targetId);
      return;
    }

    // Not on home page — navigate home first, then scroll after render
    navigate("/", { state: { scrollTo: targetId } });
  };

  // After navigating back to home, scroll to the requested section
  useEffect(() => {
    if (isHomePage && location.state?.scrollTo) {
      const targetId = location.state.scrollTo;
      setActiveSection(targetId); // Highlight immediately

      const timeout = setTimeout(() => {
        scrollToSection(targetId);
        // Clear state so refresh / back-nav doesn't re-trigger
        navigate("/", { replace: true, state: {} });
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [isHomePage, location.state, navigate]);

  const handleDownloadBrochure = () => {
    const pdfUrl = "/brochure.pdf";
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = "Siddhi Ganapathi Brochure.pdf";
    link.click();
  };

  return (
    // Semi-transparent black background with backdrop blur
    <div className="bg-white fixed top-0 left-0 w-full z-50 shadow-md text-white border-b border-white/10">
      <div className="w-full max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-6 xl:px-8 py-2.5 sm:py-3 flex justify-between items-center">
        {/* 1. LEFT: Logo + Mobile Call */}
        <div className="flex items-center shrink-0">
          <a href="/" className="flex items-center">
            <img
              src={logo}
              alt="Logo"
              className="h-10 sm:h-12 lg:h-12 xl:h-14 w-auto object-contain shrink-0"
            />
          </a>
        </div>

        {/* 2. CENTER: Navigation Links */}
        <nav className="hidden lg:flex items-center justify-center mx-auto space-x-3 xl:space-x-6 2xl:space-x-8 font-semibold text-xs xl:text-[14px] 2xl:text-[15px] whitespace-nowrap px-4 text-gray-200">
          <a
            onClick={(e) => handleScroll(e, "home")}
            className={`cursor-pointer px-1 py-1 transition ${activeSection === "home"
              ? "text-[#298C53] font-bold"
              : "hover:text-[#298C53] text-black"
              }`}
          >
            Home
          </a>
          <a
            onClick={(e) => handleScroll(e, "about")}
            className={`cursor-pointer px-1 py-1 transition ${activeSection === "about"
              ? "text-[#298C53] font-bold"
              : "hover:text-[#298C53] text-black"
              }`}
          >
            About
          </a>

          {/* Projects Dropdown */}
          <div
            className="relative py-2"
            onMouseEnter={() => setIsProjectsHovered(true)}
            onMouseLeave={() => setIsProjectsHovered(false)}
          >
            <button
              type="button"
              className={`flex items-center px-1 py-1 focus:outline-none ${["ongoingProjects", "completedProjects", "upcomingProjects"].includes(
                activeSection
              )
                ? "text-[#298C53] font-bold"
                : "hover:text-[#298C53] text-black"
                }`}
              onClick={() => setIsProjectsHovered(!isProjectsHovered)}
            >
              Projects
              <FaChevronDown
                className={`ml-1 text-[10px] xl:text-xs transition-transform duration-200 ${isProjectsHovered ? "rotate-180" : ""
                  }`}
              />
            </button>

            {/* Dropdown Items */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50 ${isProjectsHovered ? "block" : "hidden"
                }`}
            >
              <div className="bg-[#121212]/95 backdrop-blur-md border border-white/10 shadow-2xl rounded-md py-1.5 w-48 text-left">
                <p
                  onClick={(e) => {
                    handleScroll(e, "ongoingProjects");
                    setIsProjectsHovered(false);
                  }}
                  className={`px-4 py-2 hover:bg-white/10 cursor-pointer font-medium text-xs xl:text-sm transition ${activeSection === "ongoingProjects"
                    ? "text-[#298C53] font-bold bg-white/5"
                    : "text-gray-200"
                    }`}
                >
                  Ongoing Projects
                </p>
                <p
                  onClick={(e) => {
                    handleScroll(e, "completedProjects");
                    setIsProjectsHovered(false);
                  }}
                  className={`px-4 py-2 hover:bg-white/10 cursor-pointer font-medium text-xs xl:text-sm transition ${activeSection === "completedProjects"
                    ? "text-[#298C53] font-bold bg-white/5"
                    : "text-gray-200"
                    }`}
                >
                  Completed Projects
                </p>
              </div>
            </div>
          </div>

          <a
            onClick={(e) => handleScroll(e, "plots")}
            className={`cursor-pointer px-1 py-1 transition ${activeSection === "plots"
              ? "text-[#298C53] font-bold"
              : "hover:text-[#298C53] text-black"
              }`}
          >
            Layout
          </a>
          <a
            onClick={(e) => handleScroll(e, "contact")}
            className={`cursor-pointer px-1 py-1 transition ${activeSection === "contact"
              ? "text-[#298C53] font-bold"
              : "hover:text-[#298C53] text-black"
              }`}
          >
            Contact
          </a>
        </nav>

        {/* 3. RIGHT: Desktop Action Buttons */}
        <div className="hidden lg:flex items-center space-x-1.5 xl:space-x-3 shrink-0">
          <button
            onClick={handleDownloadBrochure}
            className="bg-[#298C53] hover:opacity-90 px-2.5 xl:px-4 py-1.5 rounded-lg text-white flex items-center text-xs xl:text-sm font-medium transition shadow-sm whitespace-nowrap shrink-0"
          >
            Download Brochure{" "}
            <LuDownload className="ml-1.5 text-xs xl:text-sm shrink-0" />
          </button>

          <button
            onClick={() => setIsPopupOpen(true)}
            className="bg-[#298C53] hover:opacity-90 px-2.5 xl:px-3.5 py-1.5 rounded-lg text-white text-xs xl:text-sm font-medium transition shadow-sm whitespace-nowrap shrink-0"
          >
            Book Now
          </button>

          <a
            href="tel:+919160077777"
            className="bg-[#298C53] hover:opacity-90 px-2.5 xl:px-3.5 py-1.5 rounded-lg text-white flex items-center text-xs xl:text-sm font-medium transition shadow-sm whitespace-nowrap shrink-0"
          >
            <IoCallOutline className="mr-1 text-sm xl:text-base shrink-0" />
            <span>Call Us</span>
          </a>
        </div>

        {/* Mobile/Tablet Menu Button & Actions */}
        <div className="flex items-center space-x-2 sm:space-x-3 lg:hidden">
          <button
            onClick={() => setIsPopupOpen(true)}
            className="bg-[#298C53] hover:opacity-90 text-white px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-semibold shadow-sm transition whitespace-nowrap shrink-0"
          >
            Book Now
          </button>

          <a
            href="tel:+919160077777"
            className="hidden sm:inline-flex items-center bg-[#298C53] text-white px-3 py-1.5 rounded-xl text-xs font-semibold hover:opacity-90 transition shrink-0"
          >
            <IoCallOutline className="mr-1 text-sm" />
            <span>Call Us</span>
          </a>

          <button
            className="text-2xl p-1 text-[#298C53] rounded-xl hover:text-[#298C53] transition focus:outline-none shrink-0"
            onClick={() => setIsOpen(true)}
            aria-label="Open Menu"
          >
            <FaBars />
          </button>
        </div>
      </div>

      {/* Mobile/Tablet Sidebar Drawer */}
      <div
        className={`fixed top-0 left-0 w-72 max-w-[85vw] h-full bg-white text-black shadow-2xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <img src={logo} alt="Logo" className="h-10 w-auto object-contain" />
          <IoCloseCircleOutline
            className="text-3xl text-gray-700 hover:text-black cursor-pointer"
            onClick={() => setIsOpen(false)}
          />
        </div>

        <nav className="flex flex-col flex-1 overflow-y-auto space-y-1 mt-2 px-6 pb-6 text-base">
          {navItems.map(([label, id]) => {
            const isActive = activeSection === id;

            return (
              <p
                key={id}
                onClick={(e) => handleScroll(e, id)}
                className={`py-2.5 px-3 rounded-lg border-b border-gray-100 cursor-pointer font-medium transition-all ${isActive
                  ? "bg-[#298C53]/10 text-[#298C53] font-bold border-l-4 border-l-[#298C53]"
                  : "text-gray-700 hover:text-[#298C53] hover:bg-gray-50"
                  }`}
              >
                {label}
              </p>
            );
          })}

          {/* Mobile Call Box */}
          <a
            href="tel:+919160077777"
            className="flex items-center py-3 border-b border-gray-100"
          >
            <MdLocalPhone className="text-2xl text-[#298C53] mr-2" />
            <div>
              <p className="text-xs text-gray-600 leading-tight">Call Us</p>
            </div>
          </a>

          {/* Mobile Actions: Book Now & Brochure */}
          <div className="pt-3 space-y-2.5">
            <button
              onClick={() => {
                setIsOpen(false);
                setIsPopupOpen(true);
              }}
              className="w-full bg-[#298C53] hover:opacity-90 text-white px-4 py-3 rounded-xl flex items-center justify-center font-medium shadow-md transition"
            >
              Book Now
            </button>

            <button
              onClick={handleDownloadBrochure}
              className="w-full border border-[#298C53] text-[#298C53] hover:bg-[#298C53]/10 px-4 py-3 rounded-xl flex items-center justify-center font-medium shadow-sm transition"
            >
              Download Brochure <LuDownload className="ml-2 text-lg" />
            </button>
          </div>
        </nav>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {isPopupOpen && <Popup onClose={() => setIsPopupOpen(false)} />}
    </div>
  );
};

export default Navbar;